/**
 * Os dados da sala que o cliente guarda entre mensagens.
 *
 * ## O que são, e por que perdê-los é grave
 *
 * `roomInfo` é o que o clientweb sabe sobre a conversa: o operador que atende,
 * o nome e a foto dele, e a **mensagem de encerramento configurada** no
 * departamento. Esta última é a segunda pista com que a tela reconhece o fim
 * do atendimento (ver `encerramentoDoChat`) -- sem ela, uma despedida escrita
 * como "Obrigado pelo contato!" não trava o chat nem abre a avaliação.
 *
 * Eles chegam de carona no evento de mensagem nova, como segundo argumento, e
 * o código antigo os SUBSTITUÍA inteiros a cada evento:
 *
 *     const updatedData = { ...data, CRM_id: movideskId };
 *
 * Isso apaga tudo o que não vier no evento da vez -- e há dois eventos que não
 * trazem nada:
 *
 * - **o que o próprio cliente manda.** O `sendMessage` daqui monta
 *   `operatorId`, `name` e `picture`, mas nunca `closing_message`; o servidor
 *   de ws repassa o campo como `undefined`, e ele era gravado por cima do
 *   valor bom. Ou seja: bastava o cliente escrever uma linha para a tela
 *   esquecer como reconhecer o encerramento;
 * - **o aviso que o servidor manda pela porta interna** (encerramento por
 *   inatividade, e agora a despedida do Painel). Ali o evento vai com UM
 *   argumento só -- `io.in(roomId).emit(evento, mensagens)` --, então `data` é
 *   `undefined` e `roomInfo` virava `{ CRM_id: null }`, no exato instante em
 *   que a tela mais precisava dele.
 *
 * Nada disso dá erro. A tela segue mostrando as mensagens, e o que se perde é
 * a capacidade de reconhecer o fim da conversa.
 *
 * ## Por que isto é um módulo
 *
 * Porque não há teste de componente neste repositório -- só de util --, e
 * `chat-socket` monta socket de verdade. A decisão de "o que fazer com um
 * campo que não veio" é a parte que erra em silêncio, e aqui ela tem teste.
 */

/**
 * Junta o que chegou com o que já se sabia.
 *
 * Campo ausente ou `undefined` no evento **não apaga** o valor guardado: ele
 * não veio, e não veio é diferente de mudou. `null` apaga, porque aí o
 * servidor está dizendo algo -- "esta sala não tem operador" é um fato.
 *
 * @param {object} anterior  o `roomInfo` de agora
 * @param {object} recebido  o que veio no evento (pode não vir nada)
 * @param {string|number|null} crmId  o `CRM_id` lido da última mensagem
 */
export const mesclarDadosDaSala = (anterior, recebido, crmId) => {
    const base = anterior && typeof anterior === 'object' ? anterior : {};
    const novo = { ...base };

    if (recebido && typeof recebido === 'object') {
        Object.keys(recebido).forEach((campo) => {
            if (recebido[campo] !== undefined) novo[campo] = recebido[campo];
        });
    }

    /*
     * O `CRM_id` só é sobrescrito quando se descobriu um.
     *
     * Ele é lido da última mensagem do operador, e uma lista que termina em
     * mensagem do cliente não tem nenhum -- gravar `null` ali apagaria o id
     * do chamado do Movidesk no meio da conversa, a cada linha que o cliente
     * escrevesse.
     */
    if (crmId !== null && crmId !== undefined) novo.CRM_id = crmId;

    return novo;
};

export default mesclarDadosDaSala;
